import { Component } from '@theme/component';
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/** px scrolled before the header turns solid. */
const SOLID_AT = 40;
/** px scrolled before hiding is allowed at all. */
const HIDE_AFTER = 200;
/** px of movement in one direction before the header reacts (absorbs trackpad jitter). */
const DELTA = 12;
/** ms a pointer may spend outside the header + panel region before the panel closes. */
const CLOSE_DELAY = 250;

const DESK_QUERY = window.matchMedia('(min-width: 769px)');
/** Horizon's drawers are non-modal sidebars at this width, so the bag needs our scrim. */
const DRAWER_SIDEBAR_QUERY = window.matchMedia('(min-width: 990px)');

/**
 * The Hajjo site header (sections/hajjo-header.liquid).
 *
 * - Transparent over the home hero (`data-transparent`), solid canvas once scrolled (`data-solid`),
 *   hidden while scrolling down and back on scroll up (`data-hidden`).
 * - Desk mega menu: one panel per nav link with children, opened on hover or focus, the header
 *   inverts to espresso while it is open (`data-menu-open`).
 * - Phone menu sheet: a modal <dialog> opened by MENU.
 * - Bag opens Horizon's cart drawer, Search opens Horizon's search modal, and the scrim dims the
 *   page behind the bag on desk widths where Horizon's drawer is a non-modal sidebar.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bar - The visible header bar.
 * @property {HTMLElement} scrim - The page scrim behind the panel and the bag.
 * @property {HTMLElement} [panel] - The mega menu panel.
 * @property {HTMLElement[]} [panelSets] - One link column + image per nav link with children.
 * @property {HTMLAnchorElement[]} [navLinks] - The main nav links.
 * @property {HTMLDialogElement} [sheet] - The phone menu sheet.
 * @property {HTMLButtonElement} [menuButton] - The MENU button that opens the sheet.
 * @property {HTMLElement[]} [bagCount] - Spans holding " (n)" beside each Bag word.
 *
 * @extends {Component<Refs>}
 */
class HajjoHeader extends Component {
  requiredRefs = ['bar', 'scrim'];

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  #ticking = false;
  #anchorY = 0;
  #lastY = 0;
  #solid = false;
  #hidden = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #sheetTimer = null;
  /** @type {string | null} */
  #openPanel = null;
  #bagOpen = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** Set while Escape hands focus back to a nav link, so that focus does not reopen the panel. */
  #suppressFocusOpen = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.closePanel();
    this.#bind();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
    this.#scrollTarget = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#cancelClose();
    if (this.#sheetTimer) clearTimeout(this.#sheetTimer);
    if (this.refs.sheet) unlockScroll(this.refs.sheet);
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                             */
  /* ------------------------------------------------------------------ */

  #bind() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#onScrollContainerChange, { signal });
    window.addEventListener('resize', this.#onScroll, { passive: true, signal });

    document.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    DESK_QUERY.addEventListener('change', this.#onDeskChange, { signal });

    document.addEventListener('theme-drawer:open', this.#onDrawerOpen, { signal });
    document.addEventListener('theme-drawer:close', this.#onDrawerClose, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    const { sheet } = this.refs;
    sheet?.addEventListener('close', this.#onSheetClosed, { signal });

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#publishHeight);
    this.#resizeObserver.observe(this.refs.bar);
  }

  #bindScroll() {
    const { signal } = /** @type {AbortController} */ (this.#abort);
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true, signal });
  }

  /** The scroll container switches between `.page-wrapper` (desk) and the document (phone). */
  #onScrollContainerChange = () => {
    this.#bindScroll();
    this.#onScroll();
  };

  /**
   * Publishes the header height for Horizon consumers that offset sticky content under it.
   */
  #publishHeight = () => {
    const height = this.refs.bar.offsetHeight;
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
  };

  /* ------------------------------------------------------------------ */
  /* Scroll: solid and hidden states                                    */
  /* ------------------------------------------------------------------ */

  #onScroll = () => {
    if (this.#openPanel) this.closePanel();
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => this.#update());
  };

  #update() {
    this.#ticking = false;
    const y = Math.max(getScrollTop(), 0);

    const wantSolid = y > SOLID_AT;
    if (wantSolid !== this.#solid) {
      this.#solid = wantSolid;
      this.toggleAttribute('data-solid', wantSolid);
    }

    const dy = y - this.#lastY;
    // Reset the anchor whenever direction flips, so a real reversal needs DELTA px to count.
    if ((dy > 0 && this.#anchorY > this.#lastY) || (dy < 0 && this.#anchorY < this.#lastY)) {
      this.#anchorY = this.#lastY;
    }
    const run = y - this.#anchorY;
    let wantHidden = this.#hidden;
    if (run > DELTA && y > HIDE_AFTER) wantHidden = true;
    else if (run < -DELTA || y <= HIDE_AFTER) wantHidden = false;
    if (this.#sheetIsOpen || this.#bagOpen) wantHidden = false;

    if (wantHidden !== this.#hidden) {
      this.#hidden = wantHidden;
      this.toggleAttribute('data-hidden', wantHidden);
      this.#anchorY = y;
    }
    this.#lastY = y;
  }

  /* ------------------------------------------------------------------ */
  /* Desk mega menu                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Opens the panel that belongs to the hovered or focused nav link, or closes the panel when
   * the link has none.
   * @param {Event} event - pointerenter or focus on a nav link.
   */
  onNavEnter(event) {
    if (!DESK_QUERY.matches) return;
    if (event.type === 'focus' && this.#suppressFocusOpen) return;
    const link = event.target;
    if (!(link instanceof HTMLElement)) return;

    const name = link.dataset.panel;
    if (!name) {
      this.closePanel();
      return;
    }
    this.#showPanel(name, link);
  }

  /**
   * @param {string} name - The panel's handle.
   * @param {HTMLElement} link - The nav link that owns it.
   */
  #showPanel(name, link) {
    const { panel, panelSets = [], navLinks = [] } = this.refs;
    if (!panel) return;
    const set = panelSets.find((element) => element.dataset.panel === name);
    if (!set) return;

    this.#cancelClose();
    for (const element of panelSets) element.hidden = element !== set;
    for (const navLink of navLinks) navLink.classList.toggle('is-panel-open', navLink === link);

    this.#positionPanel();
    this.toggleAttribute('data-menu-open', true);
    panel.classList.add('is-open');
    this.#openPanel = name;
    this.#syncScrim();
    // The inverted header can change height for a frame; resync the panel's top edge.
    requestAnimationFrame(() => this.#positionPanel());
  }

  /**
   * Closes the mega menu panel.
   */
  closePanel() {
    this.#cancelClose();
    const { panel, navLinks = [] } = this.refs;
    if (!this.#openPanel && !panel?.classList.contains('is-open')) return;
    panel?.classList.remove('is-open');
    this.removeAttribute('data-menu-open');
    for (const navLink of navLinks) navLink.classList.remove('is-panel-open');
    this.#openPanel = null;
    this.#syncScrim();
  }

  /** The panel hangs off the header bar's live bottom edge. */
  #positionPanel() {
    const { panel, bar } = this.refs;
    if (!panel) return;
    panel.style.top = `${Math.max(0, bar.getBoundingClientRect().bottom)}px`;
  }

  /**
   * Keeps the panel open while the pointer moves between the bar and the panel.
   */
  cancelClose() {
    this.#cancelClose();
  }

  #cancelClose() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  /**
   * Schedules a close only when the pointer lands outside both the bar and the panel.
   * @param {PointerEvent} event - pointerleave on the bar or the panel.
   */
  onRegionLeave(event) {
    if (!this.#openPanel) return;
    if (this.#inRegion(event.relatedTarget)) return;
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => this.closePanel(), CLOSE_DELAY);
  }

  /**
   * @param {EventTarget | null} node
   * @returns {boolean} Whether the node is the bar, the panel, or inside either.
   */
  #inRegion(node) {
    if (!(node instanceof Node)) return false;
    const { bar, panel } = this.refs;
    return bar.contains(node) || Boolean(panel?.contains(node));
  }

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.#openPanel) return;
    if (this.#inRegion(event.relatedTarget)) return;
    this.closePanel();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!this.#openPanel) return;

    // ArrowDown on a nav link moves into its open panel, so keyboard users reach every column.
    if (event.key === 'ArrowDown') {
      const link = event.target;
      if (!(link instanceof HTMLElement) || !link.classList.contains('is-panel-open')) return;
      const set = this.refs.panelSets?.find((element) => element.dataset.panel === link.dataset.panel);
      const first = set?.querySelector('a');
      if (!first) return;
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.key !== 'Escape') return;
    const link = this.refs.navLinks?.find((element) => element.classList.contains('is-panel-open'));
    this.closePanel();
    this.#suppressFocusOpen = true;
    link?.focus({ preventScroll: true });
    this.#suppressFocusOpen = false;
  };

  #onDeskChange = () => {
    if (DESK_QUERY.matches) this.closeSheet();
    else this.closePanel();
  };

  /* ------------------------------------------------------------------ */
  /* Scrim                                                              */
  /* ------------------------------------------------------------------ */

  #syncScrim() {
    const on = Boolean(this.#openPanel) || (this.#bagOpen && DRAWER_SIDEBAR_QUERY.matches);
    this.refs.scrim.classList.toggle('is-on', on);
  }

  /**
   * Closes whatever the scrim sits behind: the panel and the bag.
   */
  closeAll() {
    this.closePanel();
    if (this.#bagOpen) this.#cartDrawer?.close();
  }

  /* ------------------------------------------------------------------ */
  /* Bag and search                                                     */
  /* ------------------------------------------------------------------ */

  /** @returns {(HTMLElement & { open: Function, close: Function }) | null} */
  get #cartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
      return /** @type {HTMLElement & { open: Function, close: Function }} */ (drawer);
    }
    return null;
  }

  /**
   * Opens Horizon's cart drawer. Without it (cart page, page cart type, script not loaded yet)
   * the link goes to /cart as normal.
   * @param {MouseEvent} event
   */
  openBag(event) {
    const drawer = this.#cartDrawer;
    if (!drawer) return;
    event.preventDefault();
    this.closePanel();
    this.closeSheet({ restoreFocus: false });
    drawer.open();
  }

  /**
   * Opens Horizon's predictive search modal. Without it the link goes to /search.
   * @param {MouseEvent} event
   */
  openSearch(event) {
    const modal = document.getElementById('search-modal');
    if (!modal || !('showDialog' in modal) || typeof modal.showDialog !== 'function') return;
    event.preventDefault();
    this.closePanel();
    this.closeSheet({ restoreFocus: false });
    modal.showDialog();
  }

  /** @param {Event} event */
  #onDrawerOpen = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== 'cart-drawer') return;
    this.#bagOpen = true;
    this.closePanel();
    this.#syncScrim();
  };

  /** @param {Event} event */
  #onDrawerClose = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== 'cart-drawer') return;
    this.#bagOpen = false;
    this.#syncScrim();
  };

  /**
   * Repaints " (n)" beside every Bag word, the way Horizon's cart-icon does.
   * @param {Event & { promise?: Promise<any> }} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail } = {}) => {
        const count = Number(cart?.totalQuantity ?? detail?.itemCount ?? 0);
        for (const span of this.refs.bagCount ?? []) {
          span.textContent = count > 0 ? ` (${count})` : '';
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[hajjo-header] Cart update rejected:', error);
      });
  };

  /* ------------------------------------------------------------------ */
  /* Phone menu sheet                                                   */
  /* ------------------------------------------------------------------ */

  get #sheetIsOpen() {
    return Boolean(this.refs.sheet?.open);
  }

  /**
   * Opens the full-screen menu sheet (phones).
   */
  openSheet() {
    const { sheet, menuButton } = this.refs;
    if (!sheet || sheet.open) return;
    this.closePanel();
    if (this.#sheetTimer) clearTimeout(this.#sheetTimer);
    sheet.classList.remove('is-closing');
    lockScroll(sheet);
    sheet.showModal();
    menuButton?.setAttribute('aria-expanded', 'true');
    this.#update();
  }

  /**
   * Closes the menu sheet with its slide-out, then closes the dialog.
   * @param {{ restoreFocus?: boolean } | Event} [options]
   */
  closeSheet(options) {
    const { sheet } = this.refs;
    if (!sheet?.open) return;
    const restoreFocus = !(options && 'restoreFocus' in options && options.restoreFocus === false);
    sheet.dataset.restoreFocus = String(restoreFocus);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      sheet.close();
      return;
    }
    sheet.classList.add('is-closing');
    if (this.#sheetTimer) clearTimeout(this.#sheetTimer);
    this.#sheetTimer = setTimeout(() => {
      sheet.classList.remove('is-closing');
      sheet.close();
    }, 360);
  }

  /**
   * Closes the sheet when its backdrop (the dialog box outside the content) is clicked.
   * @param {MouseEvent} event
   */
  onSheetClick(event) {
    const { sheet } = this.refs;
    if (!sheet || event.target !== sheet) return;
    const rect = sheet.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) this.closeSheet();
  }

  /** Runs for every close path: the Close button, Escape, the backdrop, a breakpoint change. */
  #onSheetClosed = () => {
    const { sheet, menuButton } = this.refs;
    if (!sheet) return;
    sheet.classList.remove('is-closing');
    unlockScroll(sheet);
    menuButton?.setAttribute('aria-expanded', 'false');
    if (sheet.dataset.restoreFocus !== 'false') menuButton?.focus({ preventScroll: true });
    delete sheet.dataset.restoreFocus;
  };
}

if (!customElements.get('hajjo-header')) {
  customElements.define('hajjo-header', HajjoHeader);
}
